import { createClient, type components } from "@assessly/api-client";
import {
  PROFILE_SIGNED_OUT,
  observeTenantResponse,
  setSuspensionProfileReader,
} from "./tenant-suspension";

// The single HTTP client every screen uses (doctrine 3: apps/web consumes only
// @assessly/api-client — no hand-rolled fetch). createClient() already sets
// baseUrl "" (same-origin, the tenant subdomain) + credentials "include", so
// the httpOnly session cookie rides along automatically.
export const client = createClient();

// The one place the web learns that its tenant is suspended or closed (spec 15
// §3.2, D-495 ruling 2): every answer passes through here, and a 403 or 410
// that says so is recorded in lib/tenant-suspension.ts for the shells to read.
// The admin host never receives those codes, so the console is unaffected.
client.use({
  async onResponse({ request, response }) {
    await observeTenantResponse(request, response);
    return undefined;
  },
});
setSuspensionProfileReader(async () => {
  const result = await client.GET("/v1/auth/profile");
  if (result.response.ok) return result.data;
  return result.response.status === 401 ? PROFILE_SIGNED_OUT : undefined;
});

export type Product = components["schemas"]["Product"];
export type ProductKind = Product["kind"];
export type ProductTracking = Product["tracking"];
export type Uom = components["schemas"]["Uom"];

export const PRODUCT_KINDS: readonly ProductKind[] = [
  "finished_good",
  "semi_finished",
  "raw_material",
  "consumable",
  "service",
];

/** Traceability modes (contracts `ProductTracking`). Typed against the generated
 * schema, so adding one to the contract fails the build here rather than
 * silently dropping an option from every picker that offers them. */
export const PRODUCT_TRACKING: readonly ProductTracking[] = [
  "none",
  "lot",
  "serial",
  "measured_unit",
];
